/* The console's calls to the `optimizer` edge function, and the reads behind the
 * pending row.
 *
 * `applyMoves` never throws. The square is already up by the time it is called and
 * nothing about the board waits on it, so every way it can fail comes back as an
 * answer for js/rules/pending-moves.js to word. supabase-js reports a non-2xx with
 * the body unread, so the digging is the same as js/dreamcloud.js.
 *
 * Dry run is not here on purpose. The function reads it from the salon's settings
 * and ignores anything in the request.
 */
import { answerFromFailure } from './rules/pending-moves.js';
import { sync } from './sync.js';

var FUNCTION = 'optimizer';

// A swap is three moves, each a fresh read and a save and a read-back. Past this
// the desk is told it may have gone through. The function carries on regardless.
var TIMEOUT_MS = 60000;
// One line of Approve is one fresh read, one save and a read-back.
var LINE_TIMEOUT_MS = 45000;
// A Check now reads eight days. It carries on regardless of a screen that gave up.
var CHECK_TIMEOUT_MS = 150000;

function isOnline() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

async function invoke(body) {
  var res = await sync.sb.functions.invoke(FUNCTION, { body: body });
  if (!res.error) return res.data;
  var err = new Error(res.error.message || 'That did not work.');
  err.status = null;
  err.said = null;
  try {
    var ctx = res.error.context;
    if (ctx) {
      err.status = ctx.status || null;
      if (typeof ctx.json === 'function') {
        var answer = await ctx.json();
        err.said = (answer && answer.error) || null;
      }
    }
  } catch (e) { /* not json, or already consumed */ }
  throw err;
}

function timeout(ms) {
  return new Promise(function (_resolve, reject) {
    setTimeout(function () {
      reject(Object.assign(new Error('Timed out'), { timedOut: true }));
    }, ms);
  });
}

/* `moves` are Mangomint's ids, in the order to run. */
async function applyMoves(moves, pendingId) {
  try {
    return await Promise.race([
      invoke({ action: 'apply_moves', source: 'desk_assign', moves: moves, pending_id: pendingId }),
      timeout(TIMEOUT_MS)
    ]);
  } catch (err) {
    return answerFromFailure(err, isOnline());
  }
}

/* The Optimizer's Approve, one line at a time (approveBatches). */
async function approveMoves(moves) {
  try {
    return await Promise.race([
      invoke({ action: 'apply_moves', source: 'optimizer', moves: moves }),
      timeout(LINE_TIMEOUT_MS)
    ]);
  } catch (err) {
    return answerFromFailure(err, isOnline());
  }
}

/* The Optimizer's Check now. Never throws, for the same reason. */
async function checkNow() {
  try {
    return await Promise.race([invoke({ action: 'check_now' }), timeout(CHECK_TIMEOUT_MS)]);
  } catch (err) {
    return answerFromFailure(err, isOnline());
  }
}

/* Today's open items. Throws for the caller to swallow: a part of the schema not
 * yet applied, or a role RLS hides them from, must not touch the board. */
async function fetchPendingMoves(date) {
  var res = await sync.sb.from('tt_pending_moves').select('*').eq('date', date).is('closed_at', null);
  if (res.error) throw new Error(res.error.message);
  return res.data || [];
}

/* Whether a write key is set: true, false, or null for not knowing, which leaves
 * Swap offered and the function to say. */
async function fetchWriteKeySet() {
  var res = await sync.sb.from('tt_mangomint_api_config').select('write_key_set').maybeSingle();
  if (res.error || !res.data) return null;
  return res.data.write_key_set === true;
}

export var optimizerApi = {
  applyMoves: applyMoves,
  approveMoves: approveMoves,
  checkNow: checkNow,
  fetchPendingMoves: fetchPendingMoves,
  fetchWriteKeySet: fetchWriteKeySet
};
